import { SimpleUser } from 'app/models';
import { EquipmentType } from './equipment.model';
import { SportType } from './global.model';
import { TimelineCountry } from './timeline-country.model';

export enum TimelineElementType {
  ACTIVITY_SESSION_COMPLETED = 'activity_session_completed',
  USER_FOLLOWED = 'user_followed',
  BADGE_UNLOCKED = 'badge_unlocked',
  VIDEO_ADDED = 'video_added',
  VIDEO_COACHING_ADDED = 'video_coaching_added',
  PLAYLIST_UNLOCKED = 'playlist_unlocked',
}

export enum ActivityMode {
  DISCOVERY = 'discovery',
  CHALLENGE = 'challenge',
  INTERVAL_TRAINING = 'interval_training',
  COACHING = 'coaching',
  FREE_RIDE = 'free_ride',
  WORKOUT = 'workout',
  GPS_ONLY = 'gps_only',
  OUTDOOR = 'outdoor',
}

export type TimelineSubject =
  | ActivitySessionCompleted
  | VideoAdded
  | VideoCoachingAdded
  | UserFollowed
  | BadgeUnlocked
  | PlaylistUnlocked;

export interface TimelineThumbnails {
  small: string;
  medium: string;
  large: string;
  xlarge?: string;
}
export interface VideoCoachingAdded {
  id: number;
  access_type: string;
  author: SimpleUser;
  completion_percent: number;
  description: string;
  difficulty_level: string;
  duration: number;
  equipment_type: EquipmentType;
  hardware: string;
  hash_id: string;
  lang: TimelineCountry;
  name: string;
  ratio: number;
  sound_available: boolean;
  sport: SportType;
  status: 'public' | 'private';
  thumbnails: TimelineThumbnails;
}

export interface VideoAdded {
  id: number;
  access_type: string;
  approved: number;
  author: SimpleUser;
  avg_power: number;
  avg_speed: number;
  climb_category: number;
  completion_percent: number;
  country_start: TimelineCountry;
  description: string;
  difficulty_level: number;
  distance: number;
  duration: number;
  elevation_ascent: number;
  hash_id: string;
  interest: string;
  name: string;
  polyline: string;
  polyline_image_url: string;
  quality: string;
  ratio: number;
  rowing_forward: boolean;
  sound_available: boolean;
  sport: SportType;
  status: 'public' | 'private';
  thumbnails: TimelineThumbnails;
  tolerance: number;
  views_count: number;
}

export interface PlaylistUnlocked {
  author: SimpleUser;
  comments_count: number;
  completion_percent: 0;
  description: string;
  distance: number;
  duration: number;
  has_commented: boolean;
  has_liked: boolean;
  id: number;
  images: {
    '512x512': string;
    '1024x256': string;
  };
  is_favorite: boolean;
  likes_count: number;
  name: string;
  primary_color: string | null;
  secondary_color: string | null;
  status: string;
  type: string;
  url: string | null;
  videos_count: number;
}

export interface BadgeUnlocked {
  id: number;
  name: string;
  action: string;
  category: string;
  description: string;
  images_url: {
    locked: string;
    unlocked: string;
  };
  unlocked_date: number;
}

export type UserFollowed = SimpleUser;
export interface ActivitySessionCompleted {
  id: number;
  user_id: number;
  decathlon_id: any;
  activity_id: number;
  mode: ActivityMode;
  name: string;
  distance: number;
  duration: number;
  power: {
    min: number;
    avg: number;
    mac: number;
  };
  speed: {
    min: number;
    avg: number;
    max: number;
  };
  equipment_type: EquipmentType;
  sport: SportType;
  is_multiplayer: boolean;
  created_at: number;
  thumbnails: TimelineThumbnails;
  polyline_image_url: string;
  linkes_count: number;
  comments_count: number;
  has_liked: boolean;
  has_commented: boolean;
  video_id: number;
  interval_id: number | null;
  workout_id: number | null;
  workout_mode: string | null;
  gps_only_id: number | null;
  user: SimpleUser;
  status: 'private' | 'public';
  ratio: number;
}

export interface TimelineElementHttp {
  id: number;
  type: TimelineElementType;
  user: SimpleUser;
  created_at: number;
  updated_at: number;
  likeable: boolean;
  commentable: boolean;
  likes: number;
  comments: number;
  has_liked: boolean;
  has_commented: boolean;
  subject: TimelineSubject;
  cardType: CardType;
}

export interface TimelineElement
  extends Omit<TimelineElementHttp, 'created_at' | 'updated_at'> {
  created_at: Date;
  updated_at: Date;
}

export interface TimelineHttpResponse {
  data: Array<TimelineElementHttp>;
  links: object;
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
}

export type Feed = Array<TimelineElement>;

export type CardType = 'activity' | 'notification';
